import {
  AlertTriangle,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Square,
  Trophy,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { Navigate, useParams } from "react-router";
import { toast } from "sonner";

import { AttemptOutcome } from "@/components/attempt-outcome";
import { ButtonLink } from "@/components/button-link";
import { EmptyState, ErrorState, LoadingRows, Spinner } from "@/components/data-state";
import { LiveSession } from "@/components/live-session";
import {
  CopyableId,
  DetailsSection,
  Field,
  NeighbourLink,
  ResultTiles,
  SectionLabel,
  SessionFields,
  TrajectorySection,
} from "@/components/run-detail";
import { SessionTimingCard } from "@/components/session-timing";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAsync, usePolling } from "@/hooks/use-async";
import { useApiKey } from "@/hooks/use-auth";
import { ApiError, isSessionActive } from "@/lib/api";
import type { Challenge, SimSession } from "@/lib/api";
import { publicApi, studentApi } from "@/lib/api";
import { formatCountdown, formatRelative, formatTimestamp } from "@/lib/format";
import { attemptHref } from "@/lib/links";
import { bestRun, sessionRun } from "@/lib/scoring";

/** How often to ask after a session that may still change. */
const LIVE_POLL_MS = 1000;

/**
 * One attempt at a server-simulated challenge, from reservation to result.
 *
 * This is the page the SDK opens when a run starts, and the one every row of "My attempts"
 * leads to, so it has to be right for every stage: a reservation waiting on the SDK, a run
 * in progress (the camera and the counters), and each way a run can end -- scored, with
 * the same tiles and replay a submission gets, or not, with the reason. It polls while the
 * session is active and flips in place when it ends.
 */
export function AttemptDetailRoute() {
  const { id = "", sessionId = "" } = useParams();
  const apiKey = useApiKey();

  const challenge = useAsync(() => publicApi.challenge(id), [id, apiKey]);

  const session = usePolling<SimSession>(
    apiKey ? () => studentApi.simSession(sessionId, apiKey) : null,
    (value) => !isSessionActive(value.status),
    LIVE_POLL_MS,
    [sessionId, apiKey],
  );

  // The student's other attempts: this one's number, its neighbours, and the best it is
  // measured against. Refetched when this one's status changes, so a run that has just
  // ended is counted -- and a reservation that has just connected appears in the list.
  const siblings = useAsync(
    apiKey ? () => studentApi.simSessions(id, apiKey) : null,
    [id, apiKey, session.data?.status],
  );

  if (!apiKey) {
    return <EmptyState title="Sign in to view this attempt" />;
  }
  if (session.error) {
    if (session.error instanceof ApiError && session.error.status === 404) {
      return (
        <EmptyState
          title="No such attempt"
          description="It may have been a reservation the SDK never connected to — those lapse after fifteen minutes — or it belongs to another team."
        />
      );
    }
    return <ErrorState error={session.error} onRetry={session.refetch} />;
  }
  if (challenge.error) {
    return <ErrorState error={challenge.error} onRetry={challenge.refetch} />;
  }
  if (!session.data || !challenge.data) {
    return <LoadingRows rows={3} />;
  }

  const s = session.data;
  const ch = challenge.data;
  if (s.challenge_id !== id) {
    // A session opened under another challenge's URL would be judged by that challenge's
    // thresholds and drawn in its maze. The session knows where it belongs.
    return <Navigate to={attemptHref(s.challenge_id, s.id)} replace />;
  }

  // Oldest first, so "attempt 7" is stable as new ones arrive. A reservation is not in
  // the list yet; it will be attempt N+1 once the SDK connects.
  const ordered = [...(siblings.data ?? [])].sort((a, b) => a.started_at - b.started_at);
  const index = ordered.findIndex((x) => x.id === sessionId);
  const number = index >= 0 ? index + 1 : ordered.length + 1;
  const prev = index > 0 ? ordered[index - 1] : null;
  const next = index >= 0 && index < ordered.length - 1 ? ordered[index + 1] : null;
  const best = bestRun(ordered.map(sessionRun))?.session ?? null;
  const isBest = best?.id === sessionId;
  const live = s.status === "running";

  return (
    <div className="space-y-6">
      {/* --- header --- */}
      <div className="space-y-3">
        <ButtonLink variant="ghost" size="sm" to={`/challenges/${id}`} className="-ml-2.5">
          <ArrowLeft className="size-4" aria-hidden />
          {ch.name}
        </ButtonLink>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                Attempt {number}
                {index >= 0 && ordered.length > 1 ? (
                  <span className="text-muted-foreground font-normal"> of {ordered.length}</span>
                ) : null}
              </h1>
              <AttemptOutcome session={s} />
              {isBest && ordered.length > 1 ? (
                <Badge variant="default" className="gap-1">
                  <Trophy className="size-3" aria-hidden />
                  Your best
                </Badge>
              ) : null}
            </div>
            <p className="text-muted-foreground text-sm">
              {s.status === "pending" ? "Reserved" : "Started"}{" "}
              {formatTimestamp(s.connected_at ?? s.started_at)}
              <span className="text-muted-foreground/70">
                {" · "}
                {formatRelative(s.connected_at ?? s.started_at)}
              </span>
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            {live ? (
              <AbortButton session={s} apiKey={apiKey} onEnded={session.refetch} />
            ) : null}
            {ordered.length > 1 ? (
              <>
                <NeighbourLink
                  to={prev ? attemptHref(id, prev.id) : null}
                  label="Previous attempt"
                >
                  <ChevronLeft className="size-4" aria-hidden />
                  <span className="hidden sm:inline">Previous</span>
                </NeighbourLink>
                <NeighbourLink to={next ? attemptHref(id, next.id) : null} label="Next attempt">
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="size-4" aria-hidden />
                </NeighbourLink>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {/* --- body, by stage --- */}
      {s.status === "pending" ? (
        <Notice tone="info">
          <Spinner className="size-4" />
          <span>
            Waiting for your SDK to connect. Run your agent — it picks this reservation up —
            and the page takes over from there.
          </span>
          {s.expires_at ? (
            <span className="text-muted-foreground ml-auto text-xs">
              Lapses {formatCountdown(s.expires_at)}
            </span>
          ) : null}
        </Notice>
      ) : live ? (
        <LiveSession session={s} challenge={ch} apiKey={apiKey} />
      ) : (
        <>
          {s.status === "scored" && s.result ? (
            <ResultTiles
              challenge={ch}
              arrival={s.result.arrival}
              spl={s.result.spl}
              steps={s.result.nav_steps}
              session={s}
              score={s.result.score}
              best={
                best?.result
                  ? {
                      tier: best.result.goal_tier,
                      steps: best.result.nav_steps,
                      points: best.result.score.points,
                      at: best.ended_at ?? best.started_at,
                      href: attemptHref(id, best.id),
                    }
                  : null
              }
              isBest={isBest}
              attempts={ordered.length}
            />
          ) : (
            <EndedNotice session={s} challenge={ch} />
          )}

          {/* Three columns of decreasing importance: the plot, where the time went, and
              the record. Each is a fixed width so nothing stretches to fill. */}
          <div className="grid gap-6 lg:grid-cols-[26rem_minmax(0,1fr)] xl:grid-cols-[26rem_minmax(0,1fr)_19rem]">
            <TrajectorySection challenge={ch} jobId={s.job_id} ready apiKey={apiKey} />

            {s.steps_used > 0 ? (
              <section className="min-w-0 space-y-2">
                <SectionLabel>Timing</SectionLabel>
                <SessionTimingCard timing={s.timing} />
              </section>
            ) : null}

            <DetailsSection>
              <SessionFields session={s} challenge={ch} />
              {s.job_id ? (
                <Field label="Job id">
                  <CopyableId value={s.job_id} />
                </Field>
              ) : null}
              <Field label="Started">
                {formatTimestamp(s.connected_at ?? s.started_at)}
              </Field>
              {s.ended_at ? <Field label="Ended at">{formatTimestamp(s.ended_at)}</Field> : null}
            </DetailsSection>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */

function Notice({ tone, children }: { tone: "info" | "warn"; children: ReactNode }) {
  return (
    <div
      className={
        tone === "warn"
          ? "border-amber-600/30 bg-amber-500/5 flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 text-sm"
          : "bg-muted/40 flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 text-sm"
      }
    >
      {children}
    </div>
  );
}

/**
 * Why a run ended without a score, in the student's terms.
 *
 * The gateway's `end_reason` is precise but terse ("idle timeout"); each gets the sentence
 * that says what to do differently, because every one of these still spent an attempt.
 */
function EndedNotice({ session, challenge }: { session: SimSession; challenge: Challenge }) {
  const reason = session.end_reason ?? "";
  let what: string;
  // The reasons are the server's own words; the branches below match what it writes.
  const budget = `The ${challenge.max_steps.toLocaleString()}-step budget ran out before checkin().`;
  if (session.status === "aborted") {
    what = reason.includes("client gone")
      ? "The connection dropped and nobody reconnected within the grace period, so the run ended where it was."
      : reason.includes("from the site")
        ? "Ended with the Abort button on this page."
        : reason.includes("budget")
          ? budget
          : "Your code quit, or closed the session, before checkin().";
  } else if (session.status === "expired") {
    what = reason.includes("budget")
      ? budget
      : reason.includes("admin")
        ? "A TA ended this session."
        : reason.includes("restart")
          ? "The server restarted while the run was open."
          : "The session clock ran out before checkin().";
  } else {
    what = "Something went wrong on our side or in the protocol. If it recurs, tell a TA.";
  }
  return (
    <Notice tone="warn">
      <AlertTriangle className="size-4 shrink-0 text-amber-700 dark:text-amber-300" aria-hidden />
      <span>
        <span className="font-medium">Not scored.</span> {what}
        {session.steps_used > 0 ? ` ${session.steps_used.toLocaleString()} steps were taken.` : ""}
      </span>
      {reason ? (
        <code className="text-muted-foreground ml-auto font-mono text-xs">{reason}</code>
      ) : null}
    </Notice>
  );
}

/**
 * End a running attempt from its page. Unscored; the attempt stays spent -- said in the
 * dialog, because "abort" reads as "undo" to someone who has not read the rules. A
 * teammate sees this too: the run holds the team's slot.
 */
function AbortButton({
  session,
  apiKey,
  onEnded,
}: {
  session: SimSession;
  apiKey: string;
  onEnded: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function abort() {
    setBusy(true);
    try {
      await studentApi.abortSimSession(session.id, apiKey);
      toast.success("Attempt ended");
      onEnded();
      setOpen(false);
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed", { duration: 8000 });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Square className="size-3.5" aria-hidden />
        Abort
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End this attempt?</AlertDialogTitle>
            <AlertDialogDescription>
              The run stops where it is and is not scored. The attempt is still spent — an
              abort is not an undo. The SDK driving it is told and cannot continue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Keep running</AlertDialogCancel>
            <AlertDialogAction onClick={abort} disabled={busy}>
              End attempt
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
